import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {Card, Col, Flex, Row, Table, Typography} from 'antd'
import NumberFlow from '@number-flow/react'

import ButtonComponent from '../../../Components/Button'
import Private from '../../../Components/Private'
import Icon from '../../../Icon'
import InfoTooltip from '../../../Components/InfoTooltip/InfoTooltip'
import EmptyComponent from '../../../Components/Empty'
import {useAnimatedNumber} from '../../../hooks/useAnimatedNumber'
import {formatCurrency} from '../../../Utils/utils'
import {creditTriggersColumns, creditsBySourceColumns, getDashboardProgressRowProps} from './Table/columns'

import './KpiSection.less'
import './CreditsSection.less'

const {Text} = Typography

const money = (value) => formatCurrency(value ?? 0, true)

// The same stat as the metric tabs above, minus the affordances: no underline, no wash,
// no click. Reusing .kpi-tab keeps one definition of what a dashboard stat looks like.
const CreditStat = ({label, value, prefix, tooltip, secondary, secondaryPrivate}) => {
  const {t} = useTranslation()
  const animatedValue = useAnimatedNumber(value)

  return (
    <div className="kpi-tab static">
      <Flex gap={4} align="center">
        <Text className="kpi-tab_label" ellipsis>
          {label}
        </Text>
        {tooltip && <InfoTooltip title={t(tooltip)} />}
      </Flex>
      <Text className="kpi-tab_value">
        <Private>
          <NumberFlow value={animatedValue} prefix={prefix || ''} format={{maximumFractionDigits: 0}} />
        </Private>
      </Text>
      {secondary ? (
        <Text className="kpi-tab_secondary">
          {secondaryPrivate ? <Private>{secondary}</Private> : secondary}
        </Text>
      ) : null}
    </div>
  )
}

const buildStats = (kpis, t) => [
  {
    key: 'requests',
    label: t('Total requests'),
    value: kpis.requests_count,
    tooltip: 'Every credit request raised in this period, and the total value requested.',
    secondary: t('_amount_ requested', {amount: money(kpis.requested_amount)}),
    secondaryPrivate: true,
  },
  {
    key: 'fees_recovered',
    label: t('Fees recovered'),
    value: kpis.credited_back_amount,
    prefix: '$',
    tooltip: 'Money the networks returned, and the average per approved credit.',
    secondary: t('_amount_ average credit', {amount: money(kpis.average_credit_amount)}),
    secondaryPrivate: true,
  },
  {
    key: 'auto_approved',
    label: t('Auto approved'),
    value: kpis.auto_approved_count,
    tooltip: 'Credits the networks granted straight away, with no evidence needed from you.',
    secondary: t('_rate_% instant', {rate: kpis.auto_approved_rate ?? 0}),
  },
  {
    key: 'manually_approved',
    label: t('Manually approved'),
    value: kpis.manually_approved_count,
    tooltip: 'Credits granted after review, once the request was checked.',
    secondary: t('_rate_% of requests', {rate: kpis.manually_approved_rate ?? 0}),
  },
  {
    key: 'pending',
    label: t('Pending'),
    value: kpis.pending_count,
    tooltip: 'Requests still open: awaiting a decision, reopened, or waiting on your evidence.',
    secondary: t('_rate_% of requests', {rate: kpis.pending_rate ?? 0}),
  },
  {
    key: 'rejected',
    label: t('Rejected'),
    value: kpis.rejected_count,
    tooltip: 'Requests the networks turned down, and the share of all requests they approved.',
    secondary: t('_rate_% approval rate', {rate: kpis.approval_rate ?? 0}),
  },
]

// A distribution table with the share bar behind each row, the same one Alerts by Bank and
// Alerts by Outcome use. Rows arrive carrying `percentage` from the reducer.
const DistributionTable = ({title, columns, rows, loading}) => (
  <Flex vertical gap={16} className="credits-section_panel">
    <Text className="credits-section_panel-title">{title}</Text>
    <Table
      className="chart-table"
      dataSource={rows}
      columns={columns}
      onRow={getDashboardProgressRowProps}
      pagination={false}
      rowKey="label"
      tableLayout="fixed"
      loading={loading}
      showHeader={!!rows?.length}
      locale={{emptyText: <EmptyComponent type="no_data_available" />}}
    />
  </Flex>
)

const CreditsSection = ({summary, theme, isMobile, loading, onViewAll}) => {
  const {t} = useTranslation()

  const stats = useMemo(() => (summary?.kpis ? buildStats(summary.kpis, t) : []), [summary?.kpis, t])
  const sourceColumns = useMemo(() => creditsBySourceColumns(theme), [theme])

  const colStyle = {
    width: isMobile && '100%',
    maxWidth: isMobile && 'unset',
    flex: isMobile && 'unset',
  }

  return (
    <Card
      title={t('Credits')}
      className="card-chart-wrapper withTitle"
      styles={{header: {border: 'none'}}}
      extra={
        isMobile ? (
          <span />
        ) : (
          <ButtonComponent
            type={'link'}
            onClick={onViewAll}
            icon={<Icon type={'chevron_right_small'} />}
            iconPosition={'end'}
            style={{padding: 0}}
            size={'small'}
          >
            {t('View All')}
          </ButtonComponent>
        )
      }
    >
      <div className="credits-section">
        <div className="credits-section_stats">
          {stats.map((stat) => (
            <CreditStat key={stat.key} {...stat} />
          ))}
        </div>

        <div className="credits-section_split">
          <Row gutter={[32, 32]} align="stretch">
            <Col span={12} style={colStyle}>
              <DistributionTable
                title={t('Credit triggers')}
                columns={creditTriggersColumns}
                rows={summary?.triggers?.result}
                loading={loading}
              />
            </Col>
            <Col span={12} style={colStyle}>
              <DistributionTable
                title={t('Credits by source')}
                columns={sourceColumns}
                rows={summary?.by_source?.result}
                loading={loading}
              />
            </Col>
          </Row>
        </div>
      </div>
    </Card>
  )
}

export default CreditsSection
